import { Capabilities, PlatformType } from "@spawn/common";
import {
	Align,
	I18NextProvider,
	IAssetHandle,
	IParamHandle,
	ITextOptions,
	IOverlayOptions,
	Layout,
	Orientation,
	Overlay,
	PlaySound,
	ScreenOrientationService,
	SequenceChain,
	Sound,
	SoundResource,
	Sprite,
	Stretch,
	Text,
	TextureResource,
	View,
	ViewFixedSizeScalable,
	asset,
	destroy,
	inject,
	param,
	subscription,
	view,
} from "@spawn/core";
import { FreegameService } from "@spawn/freegames";
import { CurrencyService } from "@spawn/slot-machine";
import { Subscription } from "rxjs";
import Victor from "victor";
import { TextStyles } from "../../utils/textStyles";
import { NrgBackgroundView } from "../backgroundView";
import { FGViewUtils } from "./fgViewUtils";

/** A game subclass keeps the name "FreegameOutroView". */
@view
export class FreegameOutroView extends Overlay {
	@asset(TextureResource) public readonly freegamesOutroFrame!: IAssetHandle<TextureResource>;
	@asset(SoundResource) public readonly outroSound!: IAssetHandle<SoundResource>;

	@inject private readonly freegameService!: FreegameService;
	@inject private readonly currencyService!: CurrencyService;
	@inject private readonly screenOrientationService!: ScreenOrientationService;
	@inject private readonly i18n!: I18NextProvider;
	@inject private readonly fgViewUtils!: FGViewUtils;

	@param({ default: 0.5 }) backgroundDimValue!: IParamHandle<number>;
	@param({ default: 0.8 }) portraitContainerScale!: IParamHandle<number>;
	@param({ default: 1 }) portraitFrameScale!: IParamHandle<number>;
	@param({ default: new Victor(0, -280) }) portraitFramePosition!: IParamHandle<Victor>;
	@param({ default: new Victor(0, -280) }) portraitContainerPosition!: IParamHandle<Victor>;

	@subscription subscriptions: Subscription[] = [];

	private background!: NrgBackgroundView;
	private containerView!: View;
	private endText!: Text;
	private wonAmountText!: Text;
	private frame!: Sprite;
	private soundChain!: SequenceChain;

	constructor(options: IOverlayOptions) {
		super({ ...options, layout: new Layout(Stretch.Horizontal, Stretch.Vertical) });

		this.addBackground();
		this.addFrame();
		this.addContainerView();
		this.addEndText();
		this.addWonAmountText();
		this.playOutroSound();

		this.onOrientationChange();

		this.subscriptions.push(this.screenOrientationService.orientationChanged$.subscribe(() => this.onOrientationChange()));
	}

	@destroy
	public onDestroy(): void {
		this.soundChain.cancel();
		this.fgViewUtils.setGameConsoleVisibility(true);
	}

	public addBackground(): void {
		this.background = new NrgBackgroundView({ dimValue: this.backgroundDimValue(), textureIndex: 1 });
		this.addChild(this.background);
	}

	private addFrame(): void {
		this.frame = new Sprite(this.freegamesOutroFrame(), new Layout(Align.CenterHorizontal, Align.CenterVertical));

		this.addChild(this.frame);
	}

	private addContainerView(): void {
		this.containerView = new ViewFixedSizeScalable({
			layout: new Layout(Align.CenterHorizontal, Align.CenterVertical),
		});

		this.addChild(this.containerView);
	}

	private addEndText(): void {
		this.endText = new Text({
			...this.goldTextStyle(),
			text: this.i18n.t("general.freeRounds.title").toUpperCase(),
		});
		this.endText.layout = new Layout(Align.CenterHorizontal, Align.CenterVertical);

		this.containerView.addChild(this.endText);
	}

	private addWonAmountText(): void {
		this.wonAmountText = new Text({
			...this.goldTextStyle(),
			fontSize: 100,
			text: this.currencyService.getLocalized(this.freegameService.state?.totalWinAmount ?? 0),
		});
		this.wonAmountText.layout = new Layout(Align.CenterHorizontal, Align.CenterVertical);

		this.containerView.addChild(this.wonAmountText);
	}

	private onOrientationChange(): void {
		this.fgViewUtils.setGameConsoleVisibility(true);
		this.containerView.layout.scale = new Victor(0.65, 0.65);
		this.containerView.position = new Victor(0, -10);
		this.frame.layout.scale = new Victor(0.8, 0.8);
		this.endText.position = new Victor(0, -70);
		this.wonAmountText.position = new Victor(0, 45);

		if (this.screenOrientationService.orientation === Orientation.Portrait) {
			this.fgViewUtils.setGameConsoleVisibility(false);
			this.containerView.layout.scale = new Victor(this.portraitContainerScale(), this.portraitContainerScale());
			this.frame.position = this.portraitFramePosition().clone();
			this.frame.layout.scale = new Victor(this.portraitFrameScale(), this.portraitFrameScale());
			this.containerView.position = this.portraitContainerPosition().clone();
		} else if (
			this.screenOrientationService.orientation === Orientation.Landscape &&
			Capabilities.platformType !== PlatformType.Desktop
		) {
			this.frame.layout.scale = new Victor(1, 1);
			this.frame.position = new Victor(0, -100);
			this.containerView.position = new Victor(0, -100);
		}
	}

	// The constructor reads it, so it must not read instance fields.
	protected goldTextStyle(): ITextOptions {
		return TextStyles.Gold;
	}

	private playOutroSound(): void {
		this.soundChain = new SequenceChain(new PlaySound(new Sound(this.outroSound())));
		void this.soundChain.play();
	}
}
